import { pb } from '../pocketbase/Pocketbase'
import { normalizeString } from '../utils/stringUtils'

// ============================================================================
// Types
// ============================================================================

/**
 * Lightweight interface for homepage cards
 */
export interface IFragranceCard {
  id: string
  collectionId: string
  name: string
  slug: string
  image: string
  expand?: {
    brand?: {
      name: string
    }
  }
}

export interface IFragranceCardListResult {
  items: IFragranceCard[]
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}

// ============================================================================
// Fragrances API
// ============================================================================

/**
 * Fetches approved submissions with only the fields needed for homepage cards
 * Returns: id, name, image, and expanded brand name
 */
export async function getFragrancesForCards(
  page: number = 1,
  perPage: number = 10
): Promise<IFragranceCardListResult> {
  const result = await pb.collection('approved_submissions').getList<IFragranceCard>(page, perPage, {
    sort: '-created',
    fields: 'id,collectionId,name,slug,image,expand.brand.name',
    expand: 'brand'
  })
  return result
}


/**
 * Searches approved submissions using the search_keywords field
 * - Requires at least 2 characters
 * - Splits search query by spaces and searches as AND
 * - Normalizes each word using NFD normalization (lowercase, no accents, no spaces)
 * - Adds | prefix to match the keyword format
 * Returns: id, name, slug, image, and expanded brand name
 */
export async function searchFragrances(
  query: string,
  page: number = 1,
  perPage: number = 10
): Promise<IFragranceCardListResult> {
  const trimmedQuery = query.trim()

  // Return empty results if query is less than 2 characters
  if (trimmedQuery.length < 2) {
    return {
      items: [],
      page: 1,
      perPage,
      totalItems: 0,
      totalPages: 0
    }
  }

  // Split query by spaces and normalize each word using stringUtils
  const words = trimmedQuery
    .split(/\s+/)
    .map(word => normalizeString(word))
    .filter(word => word.length > 0) // Remove empty strings

  // If no valid words after sanitization, return empty results
  if (words.length === 0) {
    return {
      items: [],
      page: 1,
      perPage,
      totalItems: 0,
      totalPages: 0
    }
  }

  // Build filter conditions - each word must be found (AND logic)
  // Format: search_keywords ~ "%|word%"
  const filterConditions = words.map(word => `search_keywords ~ "%|${word}%"`).join(' && ')

  const result = await pb.collection('approved_submissions').getList<IFragranceCard>(page, perPage, {
    sort: '-created',
    fields: 'id,collectionId,name,slug,image,expand.brand.name',
    expand: 'brand',
    filter: filterConditions
  })

  return result
}

/**
 * Get image URL for a fragrance card
 */
export function getFragranceImageUrl(fragrance: { id: string; collectionId: string; image: string }): string {
  return pb.files.getURL(
    { id: fragrance.id, collectionId: fragrance.collectionId },
    fragrance.image
  )
}
